import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSoftphone, RINGTONES } from './SoftphoneContext';
import { isNativeApp } from '../../utils/native';
import CallScreen from './CallScreen';
import Dialer from './Dialer';

const STATUS = {
  connecting: { dot: 'bg-amber-400', label: 'shell.softphone.status.connecting' },
  registered: { dot: 'bg-emerald-500', label: 'shell.softphone.status.registered' },
  failed: { dot: 'bg-red-500', label: 'shell.softphone.status.failed' },
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const NATIVE = isNativeApp();

function useElapsed(startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  if (!startedAt) return '';
  const s = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Bottom-left phone: a small status pill while idle; expands into a call
 * card when a call rings or is placed (answer / reject / hang up / mute /
 * keypad). Renders nothing for users without a softphone.
 */
export default function SoftphonePanel() {
  const { t } = useTranslation();
  const sp = useSoftphone();
  const navigate = useNavigate();
  const [keypad, setKeypad] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [dialerOpen, setDialerOpen] = useState(false);
  const elapsed = useElapsed(sp && sp.call && sp.call.startedAt);

  if (!sp || sp.status === 'off') return null;
  const { call, status, config, error } = sp;
  const st = STATUS[status] || STATUS.connecting;
  // The app opens the full phone page (call history + keypad); the web the
  // small corner dialer.
  const openPhone = () => {
    if (status !== 'registered') return;
    if (NATIVE) navigate('/phone');
    else setDialerOpen(true);
  };

  if (!call && dialerOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-50 w-64 max-w-[calc(100vw-2rem)] rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">📞 {t('shell.softphone.dialerTitle', { extension: config && config.extension })}</span>
          <button type="button" onClick={() => setDialerOpen(false)} className="text-gray-400 hover:text-gray-600" aria-label={t('common.close')}>✕</button>
        </div>
        <Dialer onPlaced={() => setDialerOpen(false)} />
        <button type="button" onClick={() => { setDialerOpen(false); navigate('/phone'); }}
          className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 hover:underline">
          {t('shell.softphone.history.recent')} →
        </button>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <label htmlFor="sp-ringtone" className="text-gray-600 dark:text-gray-300">{t('shell.softphone.ringtone')}</label>
          <select id="sp-ringtone" value={sp.ringtone} onChange={(e) => sp.setRingtone(e.target.value)}
            className="flex-1 border rounded px-1 py-1 dark:bg-gray-900 dark:border-gray-600">
            {Object.entries(RINGTONES).map(([key, r]) => <option key={key} value={key}>{t(r.labelKey)}</option>)}
          </select>
          <button type="button" onClick={() => sp.previewRingtone(sp.ringtone)} className="border rounded px-2 py-1" aria-label={t('shell.softphone.previewRingtone')}>▶</button>
        </div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)]">
        {error && (
          <div className="mb-2 max-w-xs rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
            <span className="flex-1">{error}</span>
            <button type="button" onClick={sp.clearError} aria-label={t('shell.softphone.dismissError')} className="text-red-500">✕</button>
          </div>
        )}
        <div
          role="button"
          tabIndex={0}
          onClick={openPhone}
          onKeyDown={(e) => { if (e.key === 'Enter') openPhone(); }}
          className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 shadow border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-xs cursor-pointer hover:border-emerald-400"
          title={error || t('shell.softphone.pillTitle', { extension: config && config.extension })}
        >
          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
          <span>📞 {config && config.extension} · {t(st.label)}</span>
          {status === 'registered' && !sp.audioReady && (
            <span className="text-amber-600" title={t('shell.softphone.audioBlockedHint')}>
              · 🔇 {t('shell.softphone.clickToEnableRing')}
            </span>
          )}
          {error && (
            <button type="button" onClick={(e) => { e.stopPropagation(); sp.clearError(); }} className="text-red-600" aria-label={t('shell.softphone.dismissError')}>!</button>
          )}
        </div>
      </div>
    );
  }

  // Phones (the app, narrow windows) get a full-screen call screen.
  const narrow = typeof window !== 'undefined'
    && !!window.matchMedia?.('(max-width: 639px)')?.matches;
  if (NATIVE || narrow) return <CallScreen sp={sp} call={call} elapsed={elapsed} />;

  const ringing = call.state === 'ringing';
  const directory = sp.directory || [];
  const title = ringing ? t('shell.softphone.incomingCall') : call.state === 'calling' ? t('shell.softphone.calling') : t('shell.softphone.inCall');

  return (
    <div className="fixed bottom-4 left-4 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl shadow-xl border border-emerald-300 bg-white dark:bg-gray-800 p-4">
      <div className={`text-xs font-semibold text-emerald-700 dark:text-emerald-300 ${ringing ? 'animate-pulse' : ''}`}>
        📞 {title} {elapsed && `· ${elapsed}`}
      </div>
      <div className="mt-1 font-semibold text-gray-900 dark:text-gray-100 truncate">
        {call.name || t('shell.softphone.unknownNumber')}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400">{call.number}</div>
      {call.partyUrl && (
        <button type="button" onClick={() => navigate(call.partyUrl)} className="mt-1 text-xs text-blue-700 hover:underline">
          {t('shell.softphone.openRecord')} →
        </button>
      )}

      <div className="mt-3 flex gap-2">
        {ringing ? (
          <>
            <button type="button" onClick={sp.answer} className="flex-1 rounded-lg bg-emerald-600 text-white py-2 text-sm hover:bg-emerald-700">
              {t('shell.softphone.answer')}
            </button>
            <button type="button" onClick={sp.hangup} className="flex-1 rounded-lg bg-red-600 text-white py-2 text-sm hover:bg-red-700">
              {t('shell.softphone.reject')}
            </button>
            {!call.silenced && (
              <button type="button" onClick={sp.silenceRing} className="rounded-lg border px-2 py-2 text-sm" title={t('shell.softphone.silenceHint')}>
                🔕 {t('shell.softphone.silence')}
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={sp.toggleMute} className="flex-1 rounded-lg border py-2 text-sm">
              🎙 {call.muted ? t('shell.softphone.unmute') : t('shell.softphone.mute')}
            </button>
            <button type="button" onClick={() => setKeypad((k) => !k)} className="rounded-lg border px-3 py-2 text-sm" aria-label={t('shell.softphone.keypad')}>
              ⌨
            </button>
            {call.state === 'active' && (
              <button type="button" onClick={() => setTransferOpen((o) => !o)} className="rounded-lg border px-3 py-2 text-sm" aria-label={t('shell.softphone.transferCall')} title={t('shell.softphone.transferHint')}>
                ↪
              </button>
            )}
            <button type="button" onClick={sp.hangup} className="flex-1 rounded-lg bg-red-600 text-white py-2 text-sm hover:bg-red-700">
              {t('shell.softphone.hangUp')}
            </button>
          </>
        )}
      </div>

      {transferOpen && call.state === 'active' && (
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            sp.transfer(transferTo).then(() => setTransferOpen(false)).catch(() => {});
          }}
        >
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('shell.softphone.transferCall')}</div>
          {directory.length > 0 && (
            <select
              aria-label={t('shell.softphone.selectExtension')}
              value={directory.some((d) => d.extension === transferTo) ? transferTo : ''}
              onChange={(e) => setTransferTo(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600"
            >
              <option value="">{t('shell.softphone.selectFromList')}</option>
              {directory.map((d) => <option key={d.extension} value={d.extension}>{d.name} ({d.extension})</option>)}
            </select>
          )}
          <div className="flex gap-2">
            <input
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
              placeholder={t('shell.softphone.extensionOrNumber')}
              inputMode="tel"
              aria-label={t('shell.softphone.transferTarget')}
              className="flex-1 min-w-0 border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600"
            />
            <button type="submit" disabled={!transferTo || call.transferring} className="rounded bg-emerald-600 text-white px-3 py-1 text-sm disabled:opacity-50">
              {call.transferring ? t('shell.softphone.transferring') : t('shell.softphone.transfer')}
            </button>
          </div>
        </form>
      )}

      {keypad && !ringing && (
        <div className="mt-3 grid grid-cols-3 gap-1">
          {KEYS.map((k) => (
            <button key={k} type="button" onClick={() => sp.sendDigit(k)} className="rounded border py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
              {k}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
